/**
 * K-T-13-B — Layer-3 hook for canonical per-team efficiency.
 *
 * Thin wrapper around `kpi_efficiency_by_team(shop_id, start_at,
 * end_at)`. Returns one `TeamEfficiencyResult` per active team in the
 * shop with both display totals and the canonical-filtered ratio +
 * sample-gate fields. Holds NO local KPI math — the RPC is the source
 * of truth.
 *
 * Replaces the per-team inline reduce + Math.round((SUM(predicted) /
 * SUM(actual)) * 100) loop in `TeamAnalyticsCard.tsx`. Comeback count
 * and the `get_team_labor_totals` call stay in the component (out of
 * K-T-13-B scope — comeback logic and labor-total RPC are untouched).
 *
 * Date-range conversion: shop-local `yyyy-MM-dd` strings → UTC ISO
 * timestamps via `fromZonedTime`, mirroring the K-T-10/11/12 dimension
 * primitives.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { fromZonedTime } from 'date-fns-tz';
import type { TeamEfficiencyResult } from '../../../supabase/functions/_shared/kpiCanonical';

export interface UseTeamEfficiencyOptions {
  shopId: string;
  /** Shop-local `yyyy-MM-dd`. */
  startDate: string;
  /** Shop-local `yyyy-MM-dd`. */
  endDate: string;
  shopTimezone: string;
}

export interface UseTeamEfficiencyResult {
  data: TeamEfficiencyResult[] | null;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

/**
 * Stable query key:
 *   ['kpi', 'kpi_efficiency_by_team', shopId, startDate, endDate,
 *    shopTimezone]
 */
export function useTeamEfficiency(
  opts: UseTeamEfficiencyOptions,
): UseTeamEfficiencyResult {
  const query = useQuery({
    queryKey: [
      'kpi',
      'kpi_efficiency_by_team',
      opts.shopId,
      opts.startDate,
      opts.endDate,
      opts.shopTimezone,
    ],
    queryFn: async (): Promise<TeamEfficiencyResult[] | null> => {
      const startAt = fromZonedTime(
        `${opts.startDate}T00:00:00`,
        opts.shopTimezone,
      ).toISOString();
      const endAt = fromZonedTime(
        `${opts.endDate}T23:59:59.999`,
        opts.shopTimezone,
      ).toISOString();

      const { data, error } = await (supabase.rpc as unknown as (
        fn: string,
        args: { p_shop_id: string; p_start_at: string; p_end_at: string },
      ) => Promise<{
        data: TeamEfficiencyResult[] | null;
        error: unknown;
      }>)('kpi_efficiency_by_team', {
        p_shop_id: opts.shopId,
        p_start_at: startAt,
        p_end_at: endAt,
      });

      if (error) throw error;
      return data ?? [];
    },
    enabled: Boolean(
      opts.shopId &&
        opts.startDate &&
        opts.endDate &&
        opts.shopTimezone,
    ),
    staleTime: 5 * 60 * 1000,
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}
